import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import Modal from './Modal';
import { personalakteStyles as ps, colors } from '../features/employees/styles/personalakteStyles';
import tokenRefreshService from '../core/services/TokenRefreshService';
import LocalStorageService from '../core/services/LocalStorageService';

const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // Show warning 5 min before expiry
const CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds

export default function SessionTimeoutWarning(): React.JSX.Element | null {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [extending, setExtending] = useState(false);
  const checkTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getSecondsUntilExpiry = useCallback((): number | null => {
    return tokenRefreshService.getTimeUntilExpiry();
  }, []);

  const startCountdown = useCallback((seconds: number) => {
    setSecondsLeft(seconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const checkExpiry = useCallback(() => {
    const token = LocalStorageService.getAccessToken();
    if (!token) {
      setShowWarning(false);
      return;
    }

    const secs = getSecondsUntilExpiry();
    if (secs === null) return;

    const warningThresholdSecs = WARNING_THRESHOLD_MS / 1000;

    if (secs <= warningThresholdSecs && secs > 0 && !showWarning) {
      setShowWarning(true);
      startCountdown(secs);
    } else if (secs > warningThresholdSecs) {
      setShowWarning(false);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }
  }, [getSecondsUntilExpiry, showWarning, startCountdown]);

  useEffect(() => {
    checkTimerRef.current = setInterval(checkExpiry, CHECK_INTERVAL_MS);
    return () => {
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [checkExpiry]);

  const handleExtendSession = async () => {
    setExtending(true);
    const success = await tokenRefreshService.forceRefresh();
    setExtending(false);

    if (success) {
      setShowWarning(false);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    LocalStorageService.clearAuthData();
    window.location.href = '/login';
  };

  const formatTime = (secs: number): string => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (m > 0) return `${m}:${String(s).padStart(2, '0')}`;
    return `${s}s`;
  };

  if (!showWarning) return null;

  return (
    <Modal
      isOpen
      onClose={() => setShowWarning(false)}
      title="Session Expiring"
      size="sm"
      variant="warning"
      closeOnBackdrop={false}
      footer={
        <>
          <button
            className="hover-btn-secondary"
            onClick={handleLogout}
            style={ps.buttonSecondary}
          >
            Logout
          </button>
          <button
            className="hover-btn-primary"
            onClick={handleExtendSession}
            disabled={extending}
            style={{
              ...ps.buttonPrimary,
              opacity: extending ? 0.6 : 1,
            }}
            autoFocus
          >
            {extending ? 'Extending...' : 'Stay logged in'}
          </button>
        </>
      }
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: `${colors.warning}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: 28,
          }}
        >
          ⏳
        </div>
        <p style={{ margin: '0 0 8px', fontSize: 13, color: colors.textBody, lineHeight: 1.6 }}>
          Your session will expire in
        </p>
        <p
          style={{
            margin: '0 0 12px',
            fontSize: 28,
            fontWeight: 700,
            color: secondsLeft <= 60 ? colors.danger : colors.warning,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatTime(secondsLeft)}
        </p>
        <p style={{ margin: 0, fontSize: 12, color: colors.textMuted }}>
          Click "Stay logged in" to extend your session.
        </p>
      </div>
    </Modal>
  );
}
